import { useEffect, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useSpring } from 'framer-motion';
import Hero from './components/Hero';
import About from './components/About';
import CarMaintenanceApp from './components/CarMaintenanceApp';
import MotorsportPhotography from './components/MotorsportPhotography';
import PageLayout from './components/PageLayout';
import InfoCard from './components/InfoCard';
import { initializeAnalytics } from './utils/analytics';
import { useTheme } from './contexts/ThemeContext';
import {
  Experience,
  Projects,
  Products,
  Skills,
  Hobbies,
  Profile,
  ProfilePage,
  Contact,
  Channels,
  ChannelPage,
  Motocross
} from './components/LazyComponents';

const pageFallback = (bg: string, border: string) => (
  <div className={`h-96 ${bg}/80 animate-pulse rounded-lg mx-4 sm:mx-6 my-8 border ${border}/20`} />
);

function HomePage() {
  const { themeClasses } = useTheme();

  if (!themeClasses) {
    return <div className="min-h-screen bg-terminal-black text-teal-400">Loading...</div>;
  }

  return (
    <PageLayout withTopPadding={false}>
      <Hero />
      <About />
    </PageLayout>
  );
}

function SectionPage({ children }: { children: React.ReactNode }) {
  const { themeClasses } = useTheme();
  if (!themeClasses) {
    return <div className="min-h-screen bg-terminal-black text-teal-400">Loading...</div>;
  }
  return (
    <PageLayout>
      <Suspense fallback={pageFallback(themeClasses.bg, themeClasses.border)}>{children}</Suspense>
    </PageLayout>
  );
}

function ReplioGymFitSupportPage() {
  const { themeClasses } = useTheme();

  return (
    <div className={`min-h-screen ${themeClasses.bg} ${themeClasses.text}`}>
      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="text-3xl sm:text-4xl font-bold mb-3">Replio GymFit Support</h1>
        <p className="text-base sm:text-lg opacity-90 mb-8">
          Need help with Replio GymFit? We are here to help.
        </p>

        <section className={`rounded-2xl border ${themeClasses.border} p-6 mb-6`}>
          <h2 className="text-xl font-semibold mb-3">Contact</h2>
          <p className="mb-2">Email: support@pradappandiyan.com</p>
          <p className="opacity-80">We typically reply within 1-2 business days.</p>
        </section>

        <section className={`rounded-2xl border ${themeClasses.border} p-6 mb-6`}>
          <h2 className="text-xl font-semibold mb-3">Common Help</h2>
          <ul className="list-disc pl-5 space-y-2 opacity-90">
            <li>Program setup and muscle-based planning</li>
            <li>Workout logging issues</li>
            <li>Data and history questions</li>
            <li>Bug reports and feature suggestions</li>
          </ul>
        </section>

        <section className={`rounded-2xl border ${themeClasses.border} p-6`}>
          <h2 className="text-xl font-semibold mb-3">When contacting support</h2>
          <p className="opacity-90">
            Please include your device model, OS version, and a short description of the issue so we
            can help faster.
          </p>
        </section>
      </main>
    </div>
  );
}

function App() {
  const { themeClasses, theme } = useTheme();
  const location = useLocation();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  useEffect(() => {
    initializeAnalytics();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (!themeClasses) {
    return <div className="min-h-screen bg-terminal-black text-teal-400">Loading...</div>;
  }

  const pageVariants = {
    initial: { opacity: 0, y: 12 },
    animate: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.35, ease: [0.6, 0.05, 0.01, 0.9] }
    },
    exit: {
      opacity: 0,
      y: -8,
      transition: { duration: 0.2 }
    }
  };

  return (
    <div className={`min-h-screen ${themeClasses.bg}`}>
      <motion.div
        className={`fixed top-0 left-0 right-0 h-1 z-50 origin-left ${
          theme === 'slate'
            ? 'bg-gradient-to-r from-teal-400 via-teal-400 to-teal-400'
            : theme === 'midnight'
            ? 'bg-gradient-to-r from-sky-400 via-blue-500 to-cyan-400'
            : theme === 'studio'
            ? 'bg-gradient-to-r from-teal-700 via-sky-700 to-zinc-800'
            : 'bg-gradient-to-r from-sky-400 via-teal-400 to-cyan-400'
        }`}
        style={{ scaleX }}
      />

      <AnimatePresence mode="wait">
        <motion.div
          key={location.pathname}
          variants={pageVariants}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          <Routes location={location}>
            <Route path="/" element={<HomePage />} />
            <Route
              path="/about"
              element={
                <SectionPage>
                  <About />
                </SectionPage>
              }
            />
            <Route
              path="/experience"
              element={
                <SectionPage>
                  <Experience />
                </SectionPage>
              }
            />
            <Route
              path="/products"
              element={
                <SectionPage>
                  <Products />
                </SectionPage>
              }
            />
            <Route
              path="/channels"
              element={
                <SectionPage>
                  <Channels />
                </SectionPage>
              }
            />
            <Route
              path="/channels/:slug"
              element={
                <SectionPage>
                  <ChannelPage />
                </SectionPage>
              }
            />
            <Route
              path="/motocross"
              element={
                <SectionPage>
                  <Motocross />
                </SectionPage>
              }
            />
            <Route
              path="/projects"
              element={
                <SectionPage>
                  <Projects />
                </SectionPage>
              }
            />
            <Route
              path="/skills"
              element={
                <SectionPage>
                  <Skills />
                </SectionPage>
              }
            />
            <Route
              path="/hobbies"
              element={
                <SectionPage>
                  <Hobbies />
                </SectionPage>
              }
            />
            <Route
              path="/profile"
              element={
                <SectionPage>
                  <Profile />
                </SectionPage>
              }
            />
            <Route
              path="/contact"
              element={
                <SectionPage>
                  <Contact />
                </SectionPage>
              }
            />
            <Route
              path="/profile/:type"
              element={
                <Suspense
                  fallback={
                    <div
                      className={`min-h-screen ${themeClasses.bg} flex items-center justify-center`}
                    >
                      <div className="animate-pulse text-white">Loading...</div>
                    </div>
                  }
                >
                  <ProfilePage />
                </Suspense>
              }
            />
            <Route path="/app/car-maintenance" element={<CarMaintenanceApp />} />
            <Route
              path="/photography/motorsport"
              element={
                <SectionPage>
                  <MotorsportPhotography />
                </SectionPage>
              }
            />
            <Route path="/replio-gymfit-support" element={<ReplioGymFitSupportPage />} />
          </Routes>
        </motion.div>
      </AnimatePresence>

      <InfoCard />

      {location.pathname !== '/' && (
        <motion.button
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className={`fixed bottom-8 right-8 z-50 p-3 ${themeClasses.bg} border-2 ${themeClasses.border} ${themeClasses.text} rounded-full shadow-lg transition-all duration-300`}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </motion.button>
      )}
    </div>
  );
}

export default App;
